import React, { useState, useEffect } from "react";
import { Upload, Loader, Eye, Trash2, Download, ImagePlus, Grid, List, XCircle } from "lucide-react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { auth } from "../firebase";
import { db } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { canUseGoldFeature } from "../utils/featureAccess";
import { getStorage, ref, listAll, getDownloadURL, deleteObject } from "firebase/storage";
import { toast } from "react-toastify";
import { onSnapshot} from "firebase/firestore";

const BackgroundGenerator = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedClothing, setSelectedClothing] = useState(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [generatedImages, setGeneratedImages] = useState([]);
  const [hoveredImage, setHoveredImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [userId, setUserId] = useState(null);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(0);
  const imagesPerPage = 10;
  const [userCredits, setUserCredits] = useState(0);

  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);

  const storage = getStorage();

  // Calculate pagination
  const pageCount = Math.ceil(generatedImages.length / imagesPerPage);
  const paginatedImages = generatedImages.slice(
    (currentPage - 1) * imagesPerPage,
    currentPage * imagesPerPage
  );

  // Add pagination controls
  const Pagination = () => (
    pageCount > 1 && (
      <div className="flex flex-wrap justify-center mt-4 gap-1 sm:gap-2">
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="px-2 sm:px-3 py-1 bg-[#3913C5] text-white rounded-md disabled:opacity-50 hover:bg-[#2a0d8f] transition-colors text-sm"
        >
          Previous
        </button>
        {Array.from({ length: pageCount }, (_, i) => i + 1).map((number) => (
          <button
            key={number}
            onClick={() => setCurrentPage(number)}
            className={`px-2 sm:px-3 py-1 rounded-md text-sm ${
              currentPage === number
                ? "bg-[#3913C5] text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {number}
          </button>
        ))}
        <button
          onClick={() => setCurrentPage((p) => Math.min(pageCount, p + 1))}
          disabled={currentPage === pageCount}
          className="px-2 sm:px-3 py-1 bg-[#3913C5] text-white rounded-md disabled:opacity-50 hover:bg-[#2a0d8f] transition-colors text-sm"
        >
          Next
        </button>
      </div>
    )
  );

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const userInfo = docSnap.data();
          setUserId(user.uid);
          setUserData(userInfo);
  
          if (!canUseGoldFeature(userInfo)) {
            toast.error("This feature is only available in the Gold Plan.");
            navigate("/pricing");
          }
        }
      } else {
        setUserId(null);
        toast.error("You need to log in first.");
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, []);
  

  useEffect(() => {
    if (userId) fetchGeneratedImages(`assets/${userId}/bg-generated/`);
  }, [userId]);

  const fetchGeneratedImages = async (folder) => {
    try {
      const folderRef = ref(storage, folder);
      const res = await listAll(folderRef);
      const urls = await Promise.all(res.items.map((item) => getDownloadURL(item)));
      setGeneratedImages(urls);
    } catch (err) {
      console.error("Error fetching generated images:", err);
      toast.error("Failed to fetch generated images");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  

  const handleGenerate = async () => {
    if (!selectedFile || !prompt) {
      toast.error("Please select an image and enter a prompt");
      return;
    }
  
    try {
      // ✅ Check user credits before generating
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);
  
      if (!userSnap.exists()) {
        toast.error("User not found.");
        return;
      }
  
      const currentCredits = userSnap.data().credits || 0;
      const requiredCredits = 2; // Background generation costs 2 credits

      if (currentCredits < requiredCredits) {
        toast.error("Insufficient credits. Buy more.");
        return;
      }
  
      setProcessing(true);
      setError("");
      setGenerationProgress(0);
  
      // Simulate progress
      const progressInterval = setInterval(() => {
        setGenerationProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 1000);
  
      const formData = new FormData();
      formData.append("image", selectedFile);
      formData.append("prompt", prompt);
      formData.append("user_id", userId);
  
      const res = await fetch("https://api.styleuai.com/bg-generator/generate-background", {
      // const res = await fetch("http://127.0.0.1:5001/generate-background", {
        method: "POST",
        body: formData,
      });
  
      if (!res.ok) throw new Error("Failed to generate image");
  
      const data = await res.blob();
      const url = URL.createObjectURL(data);
      setGeneratedImages((prev) => [url, ...prev]);
      setGenerationProgress(100);
      
      // Clear preview after successful generation (like modern websites)
      setPrompt("");
      setSelectedFile(null);
      setPreview(null);
      document.getElementById("fileInput").value = null;
      
      // ✅ Deduct 2 credits
      await updateDoc(userRef, { credits: currentCredits - 2 });
      
      toast.success("Background generated successfully!");
  
    } catch (err) {
      console.error(err);
      setError("Something went wrong during generation.");
      toast.error("Something went wrong. Please try again.");
    } finally {
      setProcessing(false);
      clearInterval(generationProgress);
      setGenerationProgress(0);
      fetchGeneratedImages(`assets/${userId}/bg-generated/`);
    }
  };
  useEffect(() => {
  const user = auth.currentUser;
  if (!user) return;

  const userRef = doc(db, "users", user.uid);

  const unsubscribe = onSnapshot(userRef, (snap) => {
    if (snap.exists()) {
      const data = snap.data();
      setUserCredits(data.credits || 0);
    }
  });

  return () => unsubscribe(); // Clean up the listener on unmount
}, []);

  
const handleDownload = async (url, index) => {
  try {
    const response = await fetch("https://api.styleuai.com/download/download-image", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        image_url: url,
        filename: `StyleUAI_generated_${index}.jpg`,
      }),
    });

    if (!response.ok) throw new Error("Failed to download image");

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `StyleUAI_generated_${index}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Download error:", err);
    toast.error("Failed to download image");
  }
};

  const handleDelete = async (url) => {
    setImageToDelete(url);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!imageToDelete) return;

    try {
      const imgRef = ref(storage, imageToDelete.split("/o/")[1].split("?")[0].replace(/%2F/g, "/"));
      await deleteObject(imgRef);
      setGeneratedImages((prev) => prev.filter((item) => item !== imageToDelete));
      toast.success("Image deleted successfully");
    } catch (err) {
      console.error("Error deleting image:", err);
      toast.error("Failed to delete image");
    } finally {
      setShowDeleteDialog(false);
      setImageToDelete(null);
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
  {/* Sidebar for desktop */}
  <div className="hidden md:block">
    <Sidebar />
  </div>

  <div className="flex-1 flex flex-col">
    <Header />

    {/* Sidebar for mobile */}
    <div className="block md:hidden">
      <Sidebar />
    </div>
        <div className="p-4 sm:p-6 flex flex-col lg:flex-row gap-4 sm:gap-6 pricing-main">
          {/* Left Section */}
          <div className="w-full lg:w-1/2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Background Generator</h1>
                <p className="text-sm sm:text-base text-gray-600 mt-1">Generate stunning backgrounds using AI & custom prompts.</p>
              </div>
            </div>

            {/* Upload Box */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="relative h-64 w-full border-2 border-dashed rounded-lg bg-[#F5F2FF] flex items-center justify-center">
              {preview ? (
                <div className="relative w-full h-full">
                  <img src={preview} alt="preview" className="w-full h-full object-contain p-2" />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreview(null);
                      setSelectedFile(null);
                      document.getElementById("fileInput").value = null;
                    }}
                    className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <label htmlFor="fileInput" className="cursor-pointer text-center">
                  <Upload className="w-10 h-10 text-[#3913C5] mx-auto" />
                  <p className="text-gray-600 mt-2">Click the arrow to upload an image</p>
                  <p className="text-gray-400 text-sm">JPG, PNG Files </p>
                </label>
              )}

              <input
                type="file"
                id="fileInput"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>


              {/* Prompt Input */}
              <input
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe the desired background (e.g., beach at sunset)"
                className="mt-4 p-3 w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3913C5]"
              />

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                disabled={!selectedFile || !prompt || processing}
                className={`mt-4 w-full flex items-center justify-center bg-[#3913C5] text-white py-2 rounded-lg ${
                  (!selectedFile || !prompt || processing) && "opacity-50 cursor-not-allowed"
                }`}
              >
                {processing ? (
                  <>
                    <Loader className="animate-spin w-5 h-5 mr-2" />
                    <span>Generating... {generationProgress}%</span>
                  </>
                ) : (
                  <>
                    <ImagePlus className="w-5 h-5 mr-2" />
                    Generate Background
                  </>
                )}
              </button>

              {error && <p className="text-red-500 mt-2 text-sm">{error}</p>}
            </div>
          </div>

          {/* Right Section */}
          <div className="w-full lg:w-1/2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
                <h2 className="text-base sm:text-lg font-semibold text-gray-800">Generated Images</h2>
                <div className="flex items-center gap-2 mt-2 sm:mt-0">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-1.5 sm:p-2 rounded-md ${
                      viewMode === "grid" ? "bg-[#3913C5] text-white" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <Grid className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-1.5 sm:p-2 rounded-md ${
                      viewMode === "list" ? "bg-[#3913C5] text-white" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <List className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>
              </div>

              {viewMode === "grid" ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {paginatedImages.map((url, index) => (
                    <div
                      key={index}
                      className="relative aspect-square rounded-lg overflow-hidden cursor-pointer transition-all hover:ring-2 hover:ring-[#3913C5]"
                      onMouseEnter={() => setHoveredImage(index)}
                      onMouseLeave={() => setHoveredImage(null)}
                    >
                     <img src={url} alt="Generated" className="w-full h-full object-contain p-1" />
                      {hoveredImage === index && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-center gap-1 sm:gap-2">
                          <button
                            className="bg-white p-1.5 sm:p-2 rounded-full hover:bg-gray-200 transition-colors"
                            onClick={() => setImagePreview(url)}
                          >
                            <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            className="bg-red-600 p-1.5 sm:p-2 rounded-full hover:bg-red-700 transition-colors"
                            onClick={() => handleDelete(url)}
                          >
                            <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                          </button>
                          <button
                            className="bg-blue-600 p-1.5 sm:p-2 rounded-full hover:bg-blue-700 transition-colors"
                            onClick={() => handleDownload(url, index)}
                          >
                            <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {paginatedImages.map((url, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 sm:gap-3 p-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50"
                      onMouseEnter={() => setHoveredImage(index)}
                      onMouseLeave={() => setHoveredImage(null)}
                    >
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded overflow-hidden">
                        <img src={url} alt="Generated" className="w-full h-full object-contain p-1" />
                      </div>
                      <span className="text-xs sm:text-sm text-gray-600">Generated {index + 1}</span>
                      {hoveredImage === index && (
                        <div className="ml-auto flex items-center gap-1 sm:gap-2">
                          <button
                            className="p-1 sm:p-1.5 rounded-full hover:bg-gray-200 transition-colors"
                            onClick={() => setImagePreview(url)}
                          >
                            <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            className="p-1 sm:p-1.5 rounded-full hover:bg-red-100 transition-colors"
                            onClick={() => handleDelete(url)}
                          >
                            <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600" />
                          </button>
                          <button
                            className="p-1 sm:p-1.5 rounded-full hover:bg-blue-100 transition-colors"
                            onClick={() => handleDownload(url, index)}
                          >
                            <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <Pagination />
            </div>
          </div>
        </div>

        {/* Image Preview Modal */}
        {imagePreview && (
          <div 
            className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-75 z-50"
            onClick={() => setImagePreview(null)}
          >
            <div className="relative max-w-4xl w-full mx-4">
              <button
                className="absolute -top-10 right-0 p-2 text-white hover:text-gray-300 transition-colors"
                onClick={() => setImagePreview(null)}
              >
                <XCircle className="w-6 h-6" />
              </button>
              <img 
                src={imagePreview} 
                alt="Preview" 
                className="max-w-full max-h-[80vh] rounded-lg shadow-xl object-contain"
              />
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteDialog && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <Trash2 className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Delete Image</h3>
                <p className="text-gray-600 mb-6">Are you sure you want to delete this image? This action cannot be undone.</p>
                <div className="flex gap-4 w-full">
                  <button
                    onClick={() => {
                      setShowDeleteDialog(false);
                      setImageToDelete(null);
                    }}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BackgroundGenerator;
